import React, { useEffect, useCallback } from 'react';
import BusinessProfileSection from './sections/BusinessProfileSection';
import FundRequirementSection from './sections/FundRequirementSection';
import AssumptionsSection from './sections/AssumptionsSection';
import CashFlowTable from './sections/CashFlowTable';
import LoanProposalSection from './sections/LoanProposalSection';
import FinancialRatioSection from './sections/FinancialRatioSection';

interface CashFlowProjectionProps {
  data?: any;
  onChange?: (data: any) => void;
}

export default function CashFlowProjection({ data = {}, onChange }: CashFlowProjectionProps) {
  const [projection, setProjection] = React.useState({
    // Profil & Tujuan
    business_profile: data.business_profile || '',
    economic_sector: data.economic_sector || '',
    loan_purpose: data.loan_purpose || '',
    purpose_category: data.purpose_category || '',

    // Saldo Awal Kas & Bank
    kas_before: data.kas_before || '',
    tabungan_before: data.tabungan_before || '',
    giro_before: data.giro_before || '',

    // Saldo Awal Operasional (After)
    kas_after: data.kas_after || '',

    // Arus Kas Masuk - Before
    gaji_before: data.gaji_before || '',
    pendapatan_usaha_before: data.pendapatan_usaha_before || '',
    pendapatan_lainnya_before: data.pendapatan_lainnya_before || '',

    // Arus Kas Masuk - After
    gaji_after: data.gaji_after || '',
    pendapatan_usaha_after: data.pendapatan_usaha_after || '',
    pendapatan_lainnya_after: data.pendapatan_lainnya_after || '',

    // Penambahan Modal Kerja - Before
    penambahan_modal_before: data.penambahan_modal_before || '',

    // Beban-beban - Before
    angsuran_bank_lain_before: data.angsuran_bank_lain_before || '',
    beban_rumah_tangga_before: data.beban_rumah_tangga_before || '',
    beban_makan_before: data.beban_makan_before || '',
    beban_listrik_air_before: data.beban_listrik_air_before || '',
    beban_pendidikan_before: data.beban_pendidikan_before || '',
    beban_telekomunikasi_before: data.beban_telekomunikasi_before || '',
    beban_transportasi_before: data.beban_transportasi_before || '',
    beban_listrik_air_2_before: data.beban_listrik_air_2_before || '',
    beban_sewa_before: data.beban_sewa_before || '',
    beban_lainnya_before: data.beban_lainnya_before || '',

    // Beban-beban - After
    angsuran_bank_lain_after: data.angsuran_bank_lain_after || '',
    beban_rumah_tangga_after: data.beban_rumah_tangga_after || '',
    beban_makan_after: data.beban_makan_after || '',
    beban_listrik_air_after: data.beban_listrik_air_after || '',
    beban_pendidikan_after: data.beban_pendidikan_after || '',
    beban_telekomunikasi_after: data.beban_telekomunikasi_after || '',
    beban_transportasi_after: data.beban_transportasi_after || '',
    beban_listrik_air_2_after: data.beban_listrik_air_2_after || '',
    beban_sewa_after: data.beban_sewa_after || '',
    beban_lainnya_after: data.beban_lainnya_after || '',

    // Realisasi Kredit existing
    beban_realisasi_kredit: data.beban_realisasi_kredit || '',
    beban_provisi_pct: data.beban_provisi_pct || '',
    beban_admin: data.beban_admin || '',
    biaya_materai: data.biaya_materai || '',
    biaya_notaris: data.biaya_notaris || '',
    biaya_asuransi: data.biaya_asuransi || '',
    biaya_lainnya: data.biaya_lainnya || '',

    // Asumsi Biaya
    admin_fee_percentage: data.admin_fee_percentage || '',
    interest_type: data.interest_type || 'flat',
    repayment_method: data.repayment_method || 'flat',

    // Usulan Pinjaman
    kredit_usulan: data.kredit_usulan || '',
    repayment_capacity_pct: data.repayment_capacity_pct || '',
    suku_bunga_flat: data.suku_bunga_flat || '',
    tenor_bulan: data.tenor_bulan || '',

    calculated: data.calculated || {},
  });

  const calculateAll = useCallback(() => {
    const p = (val: any) => parseFloat(val) || 0;

    // Saldo Awal Kas & Bank - Before
    const saldoAwalBefore =
      p(projection.kas_before) + p(projection.tabungan_before) + p(projection.giro_before);

    // Saldo Awal Operasional - Before
    const saldoAwalOpBefore = saldoAwalBefore;

    // Arus Kas Masuk - Before
    const arusKasMasukBefore =
      p(projection.gaji_before) +
      p(projection.pendapatan_usaha_before) +
      p(projection.pendapatan_lainnya_before);

    // Arus Kas Keluar - Before
    const arusKasKeluarBefore =
      p(projection.penambahan_modal_before) +
      p(projection.angsuran_bank_lain_before) +
      p(projection.beban_rumah_tangga_before) +
      p(projection.beban_makan_before) +
      p(projection.beban_listrik_air_before) +
      p(projection.beban_pendidikan_before) +
      p(projection.beban_telekomunikasi_before) +
      p(projection.beban_transportasi_before) +
      p(projection.beban_listrik_air_2_before) +
      p(projection.beban_sewa_before) +
      p(projection.beban_lainnya_before);

    // Arus Kas Bersih - Before
    const arusKasBersihBefore = arusKasMasukBefore - arusKasKeluarBefore;

    // Saldo Akhir Operasional - Before
    const saldoAkhirOpBefore = saldoAwalOpBefore + arusKasBersihBefore;

    // Realisasi Kredit / Pembiayaan dari BPR
    const realisasiKredit = p(projection.beban_realisasi_kredit);
    const bebanProvisi = (realisasiKredit * p(projection.beban_provisi_pct)) / 100;
    const totalBebanRealisasi =
      bebanProvisi +
      p(projection.beban_admin) +
      p(projection.biaya_materai) +
      p(projection.biaya_notaris) +
      p(projection.biaya_asuransi) +
      p(projection.biaya_lainnya);

    // Kredit yang diberikan (usulan)
    const kreditUsulan = p(projection.kredit_usulan);

    // Calculate monthly installment for usulan
    const tenorBulan = p(projection.tenor_bulan) || 1;
    const bungaFlat = p(projection.suku_bunga_flat);
    const angsuranBunga = (kreditUsulan * bungaFlat) / 100;
    const angsuranPokok = kreditUsulan / tenorBulan;
    const angsuranPerBulan = angsuranBunga + angsuranPokok;

    // Saldo Akhir Kas & Bank - Before
    const saldoAkhirKasBefore = saldoAkhirOpBefore - totalBebanRealisasi + kreditUsulan;

    // Saldo Baki Debet - Before
    const saldoBakiDebetBefore = realisasiKredit;

    // === AFTER CALCULATION ===

    // Saldo Awal Kas & Bank - After (from calculated before)
    const saldoAwalAfter = saldoAkhirKasBefore;

    // Saldo Awal Operasional - After
    const saldoAwalOpAfter = p(projection.kas_after);

    // Arus Kas Masuk - After
    const arusKasMasukAfter =
      p(projection.gaji_after) +
      p(projection.pendapatan_usaha_after) +
      p(projection.pendapatan_lainnya_after);

    // Arus Kas Keluar - After
    const arusKasKeluarAfter =
      p(projection.angsuran_bank_lain_after) +
      p(projection.beban_rumah_tangga_after) +
      p(projection.beban_makan_after) +
      p(projection.beban_listrik_air_after) +
      p(projection.beban_pendidikan_after) +
      p(projection.beban_telekomunikasi_after) +
      p(projection.beban_transportasi_after) +
      p(projection.beban_listrik_air_2_after) +
      p(projection.beban_sewa_after) +
      p(projection.beban_lainnya_after);

    // Arus Kas Bersih - After
    const arusKasBersihAfter = arusKasMasukAfter - arusKasKeluarAfter;

    // Saldo Akhir Operasional - After
    const saldoAkhirOpAfter = saldoAwalOpAfter + arusKasBersihAfter;

    // Beban Cicilan (Repayment Capacity)
    const repaymentCapacityAmount =
      (arusKasBersihAfter * p(projection.repayment_capacity_pct)) / 100;

    // Saldo Akhir Kas & Bank - After
    const saldoAkhirKasAfter = saldoAkhirOpAfter - repaymentCapacityAmount;

    // Saldo Baki Debet - After
    const saldoBakiDebetAfter = realisasiKredit + kreditUsulan;

    // Nominal minimal saldo kas setelah pencairan
    const nominalMinimalAfter = saldoAkhirKasAfter;

    const calculated = {
      saldoAwalBefore,
      saldoAwalOpBefore,
      arusKasMasukBefore,
      arusKasKeluarBefore,
      arusKasBersihBefore,
      saldoAkhirOpBefore,
      bebanProvisi,
      totalBebanRealisasi,
      kreditUsulan,
      angsuranBunga,
      angsuranPokok,
      angsuranPerBulan,
      saldoAkhirKasBefore,
      saldoBakiDebetBefore,
      saldoAwalAfter,
      saldoAwalOpAfter,
      arusKasMasukAfter,
      arusKasKeluarAfter,
      arusKasBersihAfter,
      saldoAkhirOpAfter,
      repaymentCapacityAmount,
      saldoAkhirKasAfter,
      saldoBakiDebetAfter,
      nominalMinimalAfter,
    };

    const updatedProjection = { ...projection, calculated };
    setProjection(updatedProjection);
    if (onChange) {
      onChange(updatedProjection);
    }
  }, [projection, onChange]);

  useEffect(() => {
    calculateAll();
  }, [calculateAll]);

  const handleChange = (field: string, value: string | number) => {
    setProjection(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <BusinessProfileSection
        businessProfile={projection.business_profile}
        economicSector={projection.economic_sector}
        onChange={handleChange}
      />

      <FundRequirementSection
        loanPurpose={projection.loan_purpose}
        kreditUsulan={projection.kredit_usulan}
        purposeCategory={projection.purpose_category}
        onChange={handleChange}
      />

      <AssumptionsSection
        projection={projection}
        calculated={projection.calculated}
        onChange={handleChange}
      />

      <CashFlowTable
        projection={projection}
        calculated={projection.calculated}
        onChange={handleChange}
      />

      <LoanProposalSection
        projection={projection}
        calculated={projection.calculated}
        onChange={handleChange}
      />

      <FinancialRatioSection />
    </div>
  );
}
